import { reactive } from "vue";

/**
 * Get value at path. Path uses dot notation, e.g. "user.address.street" or "items.0.name".
 * @param {Object} obj - Source object
 * @param {string} path - Dot-separated path
 * @returns {*} Value at path or undefined
 */
export function getValueByPath(obj, path) {
  if (!path) return obj;
  return path.split(".").reduce((acc, key) => acc?.[key], obj);
}

/**
 * Set value at path. Creates objects/arrays as needed. Numeric keys create arrays.
 * @param {Object} obj - Target reactive object
 * @param {string} path - Dot-separated path (e.g. "items.0.name")
 * @param {*} value - Value to set
 */
export function setValueByPath(obj, path, value) {
  const keys = path.split(".").filter(Boolean);
  if (keys.length === 0) return;

  const lastKey = keys.pop();

  const target = keys.reduce((acc, key, i) => {
    const nextKey = keys[i + 1];
    const nextIsArrayIndex = nextKey !== undefined && /^\d+$/.test(nextKey);

    if (acc[key] == null) {
      acc[key] = nextIsArrayIndex ? [] : {};
    } else if (nextIsArrayIndex && !Array.isArray(acc[key])) {
      acc[key] = [];
    } else if (!nextIsArrayIndex && typeof acc[key] !== "object") {
      acc[key] = {};
    }
    return acc[key];
  }, obj);

  target[lastKey] = value;
}

/**
 * Create a form engine with centralized reactive state and path-based get/set.
 * No emit chains: any field updates the central state by path.
 *
 * @param {Object} initialState - Initial form data (will be made reactive)
 * @returns {{ state, getState, getValue, setValue }}
 */
export function createFormEngine(initialState = {}) {
  const state = reactive(
    typeof initialState === "object" && initialState !== null
      ? JSON.parse(JSON.stringify(initialState))
      : {}
  );

  function getValue(path) {
    return getValueByPath(state, path);
  }

  function setValue(path, value) {
    setValueByPath(state, path, value);
  }

  function getState() {
    return state;
  }

  /**
   * Replace entire state (e.g. when prop value changes from parent).
   * Merges into existing state to preserve reactivity references where possible.
   */
  function replaceState(newState) {
    if (!newState || typeof newState !== "object") return;
    const keys = new Set([
      ...Object.keys(state),
      ...Object.keys(newState),
    ]);
    keys.forEach((key) => {
      const newVal = newState[key];
      if (Array.isArray(newVal)) {
        state[key] = newVal.map((item) =>
          item && typeof item === "object" && !Array.isArray(item)
            ? { ...item }
            : item
        );
      } else if (newVal && typeof newVal === "object" && !Array.isArray(newVal)) {
        // Reemplazo completo del objeto anidado para no conservar claves viejas (ej. city que se borró)
        state[key] = JSON.parse(JSON.stringify(newVal));
      } else {
        state[key] = newVal;
      }
    });
  }

  /**
   * Add item to array at path. Path points to the array, e.g. "items".
   */
  function pushArrayItem(path, newItem) {
    const arr = getValue(path);
    const list = Array.isArray(arr) ? [...arr, newItem] : [newItem];
    setValue(path, list);
  }

  /**
   * Remove item at path and index. Path points to the array, e.g. "items".
   */
  function removeArrayItem(path, index) {
    const arr = getValue(path);
    if (!Array.isArray(arr)) return;
    const next = arr.filter((_, i) => i !== index);
    setValue(path, next);
  }

  return {
    state,
    getState,
    getValue,
    setValue,
    replaceState,
    pushArrayItem,
    removeArrayItem,
  };
}
